import React, { memo, useContext, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Data,
  GoogleMap,
  InfoWindow,
  Marker,
  Polyline,
} from '@react-google-maps/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import css from 'styled-jsx/css';

import IconsContext from '../../Context/IconsContext';
import { CONNECT_DISCONNECT_ID } from '../../Constants/StaticUUIDs';
import { isValidLat, isValidLng } from '../../Lib/AlertUtils';

export default memo(AssetPathWidget);

const { className: spinnerClass, styles: spinnerStyles } = css.resolve`
  svg {
    position: absolute;
    top: 50%;
    left: 50%;
  }
`;

const initPathPointInfoWindowState = {
  show: false,
  position: {},
  timestamp: '',
};

const initViewDistanceState = {
  distance: 0,
  endingPoint: null,
  startingPoint: null,
};

function AssetPathWidget({
  assetPathData,
  datapointID,
  fetchedData,
  signalUnitsMap,
  widgetID,
}) {
  const [recentPath, updateRecentPath] = useState([]);
  const [pathPointToShow, setPathPointToShow] = useState(
    initPathPointInfoWindowState
  );
  const [viewDistance, updateViewDistance] = useState(initViewDistanceState);

  const { offlinePin, onlinePin } = useContext(IconsContext);

  const dataLayer = useRef();
  const viewDistanceWindow = useRef();
  const viewDistancePolyline = useRef();
  const displayDistanceWindow = useRef();

  const { display_precision = 2 } =
    signalUnitsMap.get(datapointID)?.latitude ||
    signalUnitsMap.get(datapointID)?.longitude ||
    {};
  const [{ signals = {} } = {}] = fetchedData.get(CONNECT_DISCONNECT_ID) || [];
  const { value: status } =
    Object.values(signals || {}).find(
      ({ name } = {}) => name === 'connect-disconnect'
    ) || {};

  const handleMapLoaded = (map) => {
    if (!recentPath.length) return;

    const gm = window.google.maps;
    const bounds = new gm.LatLngBounds();
    recentPath.map(({ lat, lng }) => {
      const coordinate = new gm.LatLng(lat, lng);
      bounds.extend(coordinate);
    });
    map.fitBounds(bounds);
  };

  const handleMouseOverPath = (newPoint) => {
    const timestampMap = newPoint.feature.getProperty('timestampMap');
    const { latLng } = newPoint;
    if (!timestampMap || !latLng) return;

    const { computeDistanceBetween } = window.google.maps.geometry.spherical;
    const [, timestamp] =
      Array.from(timestampMap).find(
        ([ll]) => computeDistanceBetween(latLng, ll) < 20
      ) || [];
    if (!timestamp) return;

    const focusedPoint = {
      position: latLng.toJSON(),
      show: true,
      timestamp,
    };
    setPathPointToShow(focusedPoint);
  };

  const handleMouseOutOfPath = () => {
    setPathPointToShow(initPathPointInfoWindowState);
  };

  const handleDataLayerLoaded = (data) => {
    dataLayer.current = data;
    addDataLayer(recentPath);
  };

  const addDataLayer = (path = []) => {
    if (!path.length) return;

    const { coordinates, properties } = path.reduce(
      (acc, { lat, lng, timestamp }) => {
        acc.coordinates.push([lng, lat]);
        acc.properties.timestampMap.set(
          new window.google.maps.LatLng(lat, lng),
          timestamp
        );
        return acc;
      },
      { coordinates: [], properties: { timestampMap: new Map() } }
    );

    const feature = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          id: widgetID,
          geometry: {
            type: 'LineString',
            coordinates,
          },
          properties,
        },
      ],
    };
    dataLayer?.current?.forEach?.((f) => dataLayer?.current?.remove?.(f));
    dataLayer.current.addGeoJson(feature, { idPropertyName: widgetID });
  };

  const parseAssetPathData = () => {
    const parsedPath = assetPathData
      .map(({ createdAt, signals } = {}) => {
        if (!Object.keys(signals)?.length) return;

        const { latitude: lat, longitude: lng } = Object.values(
          signals || {}
        ).reduce((acc, { name = '', value } = {}) => {
          acc[name.toLowerCase()] = value;
          return acc;
        }, {});
        if (!isValidLat(lat) || !isValidLng(lng)) return;

        return { lat, lng, timestamp: createdAt };
      })
      .filter((gps) => !!gps);
    updateRecentPath(parsedPath);
    dataLayer?.current && addDataLayer(parsedPath);
  };

  const handleStartLocationClick = ({ latLng }) => {
    updateViewDistance({ ...viewDistance, startingPoint: latLng });
  };

  const handleEndLocationClick = ({ latLng }) => {
    switch (true) {
      case !viewDistance.startingPoint:
        return;
      case !!viewDistance.startingPoint && !viewDistance.endingPoint:
        updateViewDistance({ ...viewDistance, endingPoint: latLng });
        viewDistanceWindow.current?.close();
        break;
      case !!viewDistance.startingPoint && !!viewDistance.endingPoint:
        updateViewDistance(initViewDistanceState);
        break;
      default:
        return;
    }
  };

  const handleViewDistanceWindowLoaded = (window) => {
    viewDistanceWindow.current = window;
  };

  const handleViewDistanceWindowClosed = () => {
    updateViewDistance(initViewDistanceState);
  };

  const handlePolylineLoaded = (line) => {
    viewDistancePolyline.current = line;
    const distance = window.google.maps.geometry.spherical.computeLength(
      line.getPath().getArray()
    );
    updateViewDistance({ ...viewDistance, distance });
  };

  const handleDisplayDistanceWindowLoaded = (window) => {
    displayDistanceWindow.current = window;
  };

  useEffect(() => {
    parseAssetPathData();
  }, [assetPathData]);

  return (
    <GoogleMap
      clickableIcons={false}
      mapContainerClassName="h-100"
      mapContainerStyle={{
        borderBottomLeftRadius: '1rem',
        borderBottomRightRadius: '1rem',
      }}
      onClick={handleEndLocationClick}
      onLoad={handleMapLoaded}
      onRightClick={handleStartLocationClick}
      options={{ gestureHandling: 'cooperative', streetViewControl: false }}
    >
      {window && window.google && recentPath ? (
        <>
          <Data
            onLoad={handleDataLayerLoaded}
            onMouseOut={handleMouseOutOfPath}
            onMouseOver={handleMouseOverPath}
            options={{ style: { strokeColor: '#74d17d', strokeWeight: 3 } }}
          />
          <Marker
            icon={status === 'deviceConnect' ? onlinePin : offlinePin}
            position={recentPath[0]}
            zIndex={100000}
          />
          {pathPointToShow.show ? (
            <InfoWindow
              options={{ disableAutoPan: true }}
              position={pathPointToShow.position}
            >
              <div className="container text-text-gray">
                <div className="row">
                  <p className="col font-weight-bold mb-2 text-center">
                    {moment(pathPointToShow.timestamp).format('M-D-Y')}
                  </p>
                </div>
                <div className="row">
                  <p className="col font-weight-bold mb-2 text-center">
                    {moment(pathPointToShow.timestamp).format('HH:mm:ss')}
                  </p>
                </div>
                <div className="row">
                  <div className="col font-weight-bold text-center">
                    {`${Number(pathPointToShow.position.lat).toFixed(
                      2
                    )}, ${Number(pathPointToShow.position.lng).toFixed(2)}`}
                  </div>
                </div>
              </div>
            </InfoWindow>
          ) : null}
          {viewDistance.startingPoint ? (
            <InfoWindow
              onCloseClick={handleViewDistanceWindowClosed}
              onLoad={handleViewDistanceWindowLoaded}
              options={{ disableAutoPan: true, maxWidth: 150 }}
              position={viewDistance.startingPoint}
            >
              <div className="row no-gutters flex-wrap text-dark">
                <p className="col mb-0 text-center">
                  Click another point on the map to view distance.
                </p>
              </div>
            </InfoWindow>
          ) : null}
          {viewDistance.startingPoint && viewDistance.endingPoint ? (
            <Polyline
              draggable={false}
              editable={false}
              onLoad={handlePolylineLoaded}
              options={{ geodesic: true }}
              path={[viewDistance.startingPoint, viewDistance.endingPoint]}
              visible
            />
          ) : null}
          {viewDistance.startingPoint &&
          viewDistance.endingPoint &&
          viewDistance.distance ? (
            <InfoWindow
              onCloseClick={handleViewDistanceWindowClosed}
              onLoad={handleDisplayDistanceWindowLoaded}
              options={{ disableAutoPan: true }}
              position={viewDistance.endingPoint}
            >
              <div className="container-fluid">
                <div className="row no-gutters text-dark">
                  <p className="col-12 mb-2 text-center">
                    <span className="font-weight-light mr-2">Starting:</span>
                    <span>
                      {viewDistance.startingPoint.toUrlValue(display_precision)}
                    </span>
                  </p>
                </div>
                <div className="row no-gutters text-dark">
                  <p className="col-12 mb-2 text-center">
                    <span className="font-weight-light mr-2">Ending:</span>
                    <span>
                      {viewDistance.endingPoint.toUrlValue(display_precision)}
                    </span>
                  </p>
                </div>
                <div className="row no-gutters text-dark">
                  <p className="col-12 mb-2 text-center">
                    <span className="font-weight-light mr-2">Distance:</span>
                    <span>{`${Number(viewDistance.distance).toFixed(
                      2
                    )} m`}</span>
                  </p>
                </div>
              </div>
            </InfoWindow>
          ) : null}
        </>
      ) : (
        <FontAwesomeIcon
          className={spinnerClass}
          color="var(--brand-primary)"
          icon={faCircleNotch}
          size="2x"
          spin
        />
      )}
      {spinnerStyles}
    </GoogleMap>
  );
}

AssetPathWidget.propTypes = {
  assetPathData: PropTypes.arrayOf(PropTypes.object),
  datapointID: PropTypes.string,
  fetchedData: PropTypes.object,
  signalUnitsMap: PropTypes.object,
  widgetID: PropTypes.string,
};

AssetPathWidget.defaultProps = { assetPathData: [] };
